import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import GrainIcon from '@mui/icons-material/Grain';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_DENOISE_STATE,
  buildDenoiseRequest,
  hasSourceImage,
  type DenoiseFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  onResult?: (result: OperationResult) => void;
}

export const DenoisePanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  onResult,
}) => {
  const [state, setState] = useState<DenoiseFormState>(DEFAULT_DENOISE_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.denoise);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to denoise.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildDenoiseRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <GrainIcon fontSize="small" />
        <Typography variant="subtitle2">Denoise</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to denoise.</Alert>
      )}

      <Box>
        <Typography variant="caption" color="text.secondary">
          Strength · {state.strength.toFixed(2)}
        </Typography>
        <Slider
          value={state.strength}
          onChange={(_, v) => setState(s => ({ ...s, strength: Array.isArray(v) ? v[0] : v }))}
          min={0}
          max={1}
          step={0.05}
          size="small"
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={state.preserveDetails}
            onChange={e => setState(s => ({ ...s, preserveDetails: e.target.checked }))}
            size="small"
          />
        }
        label="Preserve fine details"
      />
      <FormControlLabel
        control={
          <Switch
            checked={state.faceAware}
            onChange={e => setState(s => ({ ...s, faceAware: e.target.checked }))}
            size="small"
          />
        }
        label="Face-aware denoising"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Denoise failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <GrainIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Denoising…' : 'Denoise'}
        </Button>
      </Box>
    </Stack>
  );
};
